import React from "react";
import {
  Box,
  Heading,
  Text,
  VStack,
  List,
  ListItem,
  ListIcon,
} from "@chakra-ui/react";
import { FaCheckCircle, FaTools } from "react-icons/fa";

const ResetBreakerPage = () => {
  return (
    <Box minH="100vh" bg="white">
      {/* Hero Section */}
      <Box
        bg="white"
        py={{ base: 16, md: 20 }}
        px={{ base: 6, md: 12 }}
        textAlign="center"
      >
        <Heading
          as="h1"
          fontSize={{ base: "4xl", md: "5xl" }}
          fontWeight="800"
          color="gray.900"
          mb={6}
          fontFamily="'Work Sans', sans-serif"
        >
          How to Reset a Tripped Breaker
        </Heading>
        <Text
          fontSize={{ base: "lg", md: "xl" }}
          color="gray.600"
          maxW="2xl"
          mx="auto"
        >
          A tripped breaker can cause power outages in your home. Learning how
          to reset a tripped breaker is a simple yet essential skill for every
          homeowner. Follow this step-by-step guide to restore power safely!
        </Text>
      </Box>

      {/* Content Section */}
      <Box bg="gray.50" py={{ base: 12, md: 16 }} px={{ base: 6, md: 12 }}>
        <Box maxW="1000px" mx="auto">
          {/* Tools & Materials Section */}
          <Box
            mb={12}
            p={8}
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="xl"
            boxShadow="sm"
          >
            <Heading
              as="h2"
              fontSize="2xl"
              mb={4}
              color="gray.900"
              fontWeight="700"
            >
              Tools & Materials You'll Need
            </Heading>
            <List spacing={3}>
              <ListItem color="gray.700">
                <ListIcon as={FaTools} color="orange.500" />
                Safety Gloves
              </ListItem>
              <ListItem color="gray.700">
                <ListIcon as={FaTools} color="orange.500" />
                Flashlight (if needed)
              </ListItem>
              <ListItem color="gray.700">
                <ListIcon as={FaTools} color="orange.500" />
                Screwdriver (if the breaker box is locked)
              </ListItem>
              <ListItem color="gray.700">
                <ListIcon as={FaTools} color="orange.500" />
                Circuit Tester (optional)
              </ListItem>
            </List>
          </Box>

          {/* Step-by-Step Instructions */}
          <VStack align="start" spacing={8} mb={8}>
            {/* Step 1 */}
            <Box>
              <Heading
                as="h2"
                fontSize="2xl"
                color="gray.900"
                fontWeight="700"
                mb={2}
              >
                Step 1: Identify the Tripped Breaker
              </Heading>
              <Text fontSize="lg" color="gray.600" mb={4}>
                Open your breaker box and look for the breaker that is in the
                “off” position or not fully in line with the others. This is the
                tripped breaker.
              </Text>
            </Box>

            {/* Step 2 */}
            <Box>
              <Heading
                as="h2"
                fontSize="2xl"
                color="gray.900"
                fontWeight="700"
                mb={2}
              >
                Step 2: Wear Safety Gear
              </Heading>
              <Text fontSize="lg" color="gray.600" mb={4}>
                Before touching the breaker, put on safety gloves to protect
                yourself from any electrical hazards.
              </Text>
            </Box>

            {/* Step 3 */}
            <Box>
              <Heading
                as="h2"
                fontSize="2xl"
                color="gray.900"
                fontWeight="700"
                mb={2}
              >
                Step 3: Reset the Breaker
              </Heading>
              <Text fontSize="lg" color="gray.600" mb={4}>
                To reset the breaker, push it all the way to the “off” position,
                then firmly switch it back to the “on” position. You should hear
                a click.
              </Text>
            </Box>

            {/* Step 4 */}
            <Box>
              <Heading
                as="h2"
                fontSize="2xl"
                color="gray.900"
                fontWeight="700"
                mb={2}
              >
                Step 4: Test the Circuits
              </Heading>
              <Text fontSize="lg" color="gray.600" mb={4}>
                After resetting the breaker, check the affected circuits to see
                if the power is restored. If the breaker trips again, there may
                be an underlying issue that needs to be addressed by a
                professional.
              </Text>
            </Box>
          </VStack>
        </Box>
      </Box>

      {/* Final Message Section */}
      <Box
        bg="white"
        py={{ base: 12, md: 16 }}
        px={{ base: 6, md: 12 }}
        textAlign="center"
      >
        <Text
          fontSize={{ base: "xl", md: "2xl" }}
          fontWeight="600"
          color="gray.800"
          maxW="3xl"
          mx="auto"
        >
          Great job! You’ve successfully reset a tripped breaker. If problems
          persist, consult a qualified electrician for further assistance.
        </Text>
      </Box>
    </Box>
  );
};

export default ResetBreakerPage;
